import type { Element } from 'hast';
import type { Components } from 'react-markdown';

import ReactMarkdown from 'react-markdown';
import { css, html } from 'react-strict-dom';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { remarkSpacedParagraphs, remarkTicketLinks } from '../util';
import AccentTextLink from './AccentTextLink';
import Text from './Text';

type Props = Readonly<{
  body: string | null;
  emptyText?: string;
}>;

export default function MarkdownText({
  body,
  emptyText = 'No description provided',
}: Props) {
  if (!body) {
    return (
      <Text as="p" style={styles.empty}>
        {emptyText}
      </Text>
    );
  }

  return (
    <html.div style={styles.container}>
      <ReactMarkdown
        remarkPlugins={[
          remarkBreaks,
          remarkTicketLinks,
          remarkSpacedParagraphs,
          // Needed for converting inline URLs to links
          remarkGfm,
        ]}
        components={components}
      >
        {body}
      </ReactMarkdown>
    </html.div>
  );
}

// react-markdown emits raw DOM, so each element is mapped onto a Text/RSD
// element to keep the descendant styling in css.create.
const components: Components = {
  p: ({ children, node }) => (
    <Text
      as="p"
      style={[styles.paragraph, isSpaced(node) && styles.spacedParagraph]}
    >
      {children}
    </Text>
  ),
  a: ({ children, href }) => (
    <AccentTextLink href={href}>{children}</AccentTextLink>
  ),
  strong: ({ children }) => (
    <Text as="strong" bold>
      {children}
    </Text>
  ),
  b: ({ children }) => (
    <Text as="b" bold>
      {children}
    </Text>
  ),
  em: ({ children }) => (
    <Text as="em" italic>
      {children}
    </Text>
  ),
  h1: ({ children }) => (
    <Text as="h1" style={styles.h1}>
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text as="h2" style={styles.h2}>
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text as="h3" style={styles.h3}>
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text as="h4" style={styles.h4}>
      {children}
    </Text>
  ),
  h5: ({ children }) => (
    <Text as="h5" style={styles.h5}>
      {children}
    </Text>
  ),
  h6: ({ children }) => (
    <Text as="h6" style={styles.h6}>
      {children}
    </Text>
  ),
  ul: ({ children }) => <html.ul style={styles.list}>{children}</html.ul>,
  ol: ({ children }) => <html.ol style={styles.list}>{children}</html.ol>,
  li: ({ children }) => (
    <Text as="li" style={styles.listItem}>
      {children}
    </Text>
  ),
  hr: () => <html.hr style={styles.rule} />,
  blockquote: ({ children }) => (
    <Text as="blockquote" style={styles.blockquote}>
      {children}
    </Text>
  ),
};

function isSpaced(node: Element): boolean {
  return node.properties?.['data-spaced'] === 'true';
}

const styles = css.create({
  container: {
    // No design-system equivalent for the markdown text colour.
    color: '#333',
  },
  empty: {
    color: '#999',
  },
  paragraph: {
    marginTop: 0,
    marginBottom: 0,
  },
  h1: {
    fontSize: 30,
    fontWeight: '700',
  },
  h2: {
    fontSize: 22.5,
    fontWeight: '700',
  },
  h3: {
    fontSize: 17.5,
    fontWeight: '700',
  },
  h4: {
    fontSize: 15,
    fontWeight: '700',
  },
  h5: {
    fontSize: 12.5,
    fontWeight: '700',
  },
  h6: {
    fontSize: 11,
    fontWeight: '700',
  },
  spacedParagraph: {
    marginTop: 12,
  },
  list: {
    // RSD removes the default marker and padding.
    listStyleType: 'disc',
    paddingLeft: 26,
    marginBlock: '1em',
  },
  listItem: {
    marginTop: 4,
    marginBottom: 4,
  },
  rule: {
    borderWidth: 0,
    // No design-system equivalent for the markdown rule colour.
    backgroundColor: '#ddd',
    height: 2,
    // Browser default rule margin, which RSD zeroes.
    marginTop: '0.5em',
    marginBottom: '0.5em',
  },
  blockquote: {
    // Color.primaryLight at 67% alpha; there is no token for the alpha variant.
    borderLeftWidth: 4,
    borderLeftStyle: 'solid',
    borderLeftColor: '#543e86aa',
    // Browser default quote margin, which RSD zeroes.
    marginTop: '1em',
    marginBottom: '1em',
    marginLeft: 0,
    marginRight: 40,
    paddingLeft: 15,
  },
});
